import { useEffect, useRef, useState } from 'react';
import { m } from 'motion/react';
import { formatBytes } from '../core/format';
import { engine, useApp } from '../state/store';
import { Queue } from './Queue';
import { ClipboardText } from './parts/Icons';

export function Hero() {
  const records = useApp((s) => Object.keys(s.records).length);
  return (
    <m.div
      className="hero"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.25 }}
    >
      <section className="hero__main" aria-labelledby="hero-title">
        <h1 className="hero__title" id="hero-title">
          <m.span
            className="hero__line"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            Download it once.
          </m.span>
          <m.span
            className="hero__line hero__line--soft"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.09, ease: [0.16, 1, 0.3, 1] }}
          >
            Enjoy it as it arrives.
          </m.span>
        </h1>
        <p className="hero__lede">
          Paste a direct link to a video, audio file, image, PDF or text document. OneFlow saves
          each byte once on this device and lets you watch or read the file while the rest comes in.
        </p>
        <SourceForm />
        <div className="hero__sub">
          <TrySample />
          <PanelLink />
        </div>
      </section>
      <aside className="hero__side" aria-label="This browser and your files">
        <CapabilityPanel />
        {records > 0 && <Queue compact />}
      </aside>
    </m.div>
  );
}

function PanelLink() {
  const setPanel = useApp((s) => s.setPanel);
  return (
    <button type="button" className="linkbtn" onClick={() => setPanel('cors')}>
      What counts as a direct link?
    </button>
  );
}

function TrySample() {
  const submit = useApp((s) => s.submit);
  return (
    <button
      type="button"
      className="btn"
      onClick={() => void submit(`${location.origin}/samples/oneflow-sample.mp4`)}
    >
      Try a 12-second sample
    </button>
  );
}

export function SourceForm() {
  const url = useApp((s) => s.url);
  const setUrl = useApp((s) => s.setUrl);
  const submit = useApp((s) => s.submit);
  const error = useApp((s) => s.urlError);
  const starting = useApp((s) => s.starting);
  const input = useRef<HTMLInputElement>(null);
  const [canPaste, setCanPaste] = useState(false);

  useEffect(() => {
    setCanPaste(!!navigator.clipboard?.readText);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (
        e.key === '/' &&
        !(e.target instanceof HTMLInputElement) &&
        !(e.target instanceof HTMLTextAreaElement)
      ) {
        e.preventDefault();
        input.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const paste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setUrl(text.trim());
      input.current?.focus();
    } catch {
      input.current?.focus();
    }
  };

  return (
    <form
      className="source-form"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      noValidate
    >
      <label className="source-form__label" htmlFor="source-url">
        Direct file link
      </label>
      <div className={`field ${error ? 'field--error' : ''}`}>
        <input
          id="source-url"
          ref={input}
          className="field__input mono"
          type="text"
          inputMode="url"
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          placeholder="https://example.org/film.mp4"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? 'source-error source-help' : 'source-help'}
        />
        {canPaste && (
          <button
            type="button"
            className="btn btn--quiet field__paste"
            onClick={() => void paste()}
          >
            <ClipboardText /> Paste
          </button>
        )}
        <button
          type="submit"
          className="btn btn--accent field__go"
          disabled={starting || url.trim() === ''}
        >
          {starting ? 'Starting…' : 'Start'}
        </button>
      </div>
      {error && (
        <p className="source-form__error" id="source-error" role="alert">
          <strong>{error.summary}</strong> {error.action}
        </p>
      )}
      <p className="source-form__help" id="source-help">
        Works with direct https links that let browsers read the file. It cannot open pages from
        video sites. Press <kbd>/</kbd> to focus.
      </p>
    </form>
  );
}

function Dot({ on }: { on: boolean }) {
  return <span className={`cap__dot ${on ? 'cap__dot--on' : ''}`} aria-hidden="true" />;
}

/** Live probes of this browser, never assumptions about a brand. RUN-005 */
function CapabilityPanel() {
  const c = engine.caps;
  const [est, setEst] = useState<{
    usage: number | null;
    quota: number | null;
    persisted: boolean | null;
  } | null>(null);
  const setPanel = useApp((s) => s.setPanel);

  useEffect(() => {
    let alive = true;
    void engine.storageEstimate().then((e) => alive && setEst(e));
    return () => {
      alive = false;
    };
  }, []);

  const codecs: [string, boolean][] = [
    ['H.264', c.codecs.h264],
    ['AAC', c.codecs.aac],
    ['VP9', c.codecs.vp9],
    ['AV1', c.codecs.av1],
    ['Opus', c.codecs.opus],
  ];

  return (
    <section className="caps" aria-labelledby="caps-title">
      <h2 className="caps__title" id="caps-title">
        This browser
      </h2>
      <ul className="caps__list">
        <li className="cap">
          <Dot on={c.opfs && c.workers} />
          <div>
            <p className="cap__name">Local file storage</p>
            <p className="cap__note">
              {c.opfs && c.workers
                ? 'Bytes are written to this device as they arrive.'
                : 'Not available. OneFlow cannot run here.'}
            </p>
          </div>
        </li>
        <li className="cap">
          <Dot on={c.mse} />
          <div>
            <p className="cap__name">Play while downloading</p>
            <p className="cap__note">
              {c.mse
                ? c.codecs.h264 || c.codecs.vp9 || c.codecs.av1
                  ? 'Streaming playback is available.'
                  : 'Streaming works, but no common video codec was found.'
                : 'Video plays after the download finishes.'}
            </p>
            <ul className="cap__tags">
              {codecs.map(([n, on]) => (
                <li key={n} className={`tag ${on ? 'tag--on' : ''}`}>
                  {n}
                </li>
              ))}
            </ul>
          </div>
        </li>
        <li className="cap">
          <Dot on={c.savePicker} />
          <div>
            <p className="cap__name">Saving a finished file</p>
            <p className="cap__note">
              {c.savePicker
                ? 'Streams straight to a location you pick.'
                : 'Hands the saved file to your browser to download.'}
            </p>
          </div>
        </li>
        <li className="cap">
          <Dot on={c.serviceWorker} />
          <div>
            <p className="cap__name">Works offline</p>
            <p className="cap__note">
              {c.serviceWorker
                ? 'The app opens offline. Finished files stay on this device.'
                : 'Offline start is not available here.'}
              {c.standalone ? ' Installed.' : ''}
            </p>
          </div>
        </li>
        {est?.quota != null && (
          <li className="cap">
            <Dot on />
            <div>
              <p className="cap__name">Space</p>
              <p className="cap__note mono">
                {formatBytes(est.usage)} used of about {formatBytes(est.quota)}
                {est.persisted ? ' · kept' : ''}
              </p>
            </div>
          </li>
        )}
      </ul>
      {(c.ios || !c.standalone) && (
        <button type="button" className="linkbtn" onClick={() => setPanel('install')}>
          Install OneFlow
        </button>
      )}
    </section>
  );
}
